import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import axios from 'axios'
import { getImageUrl } from '../utils/cdnUtils'
import { lotteryService, type LotteryRound } from '../services/lotteryService'
import { getTenantIdFromConfig } from '../utils/tenantUtils'
import { useTenantThemeConfig } from '../hooks/useTenantThemeConfig'
import { withApiBaseUrl } from '../lib/apiBaseUrl'

interface YeekeeResultData {
  sumTotal: string
  position16Number?: string
  finalNumber?: string
  threeDigitTop: string
  twoDigitTop?: string
  twoDigitDown?: string
  threeDigitBottom?: string
  totalShots?: number
  shotsUsedInResult?: number
  calculatedAt?: string
}

interface YeekeeShotRow {
  _id?: string
  number: string
  username?: string
  displayPhoneNumber?: string
  shooterType?: string
  shotAt: string
  position?: number
}

const YeekeeResultPage: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { themeConfig } = useTenantThemeConfig()
  const tenantId = getTenantIdFromConfig(themeConfig)
  const stateRound = (location.state as { round?: LotteryRound })?.round
  const [round, setRound] = useState<LotteryRound | undefined>(stateRound)
  const [loading, setLoading] = useState(!stateRound)
  const [shots, setShots] = useState<YeekeeShotRow[]>([])
  const [loadingShots, setLoadingShots] = useState(false)

  const roundId = round?._id || stateRound?._id
  const result = (round?.yeekee as { result?: YeekeeResultData })?.result || stateRound?.yeekee?.result

  useEffect(() => {
    const roundIdFromQuery = new URLSearchParams(location.search).get('roundId')
    if (stateRound) {
      setRound(stateRound)
      setLoading(false)
      lotteryService.getRoundById(stateRound._id).then((r) => {
        if (r && (r as any).yeekee?.result) setRound(r)
      }).catch(() => {})
      return
    }
    if (roundIdFromQuery) {
      lotteryService.getRoundById(roundIdFromQuery).then((r) => {
        if (r) setRound(r)
        setLoading(false)
      }).catch(() => setLoading(false))
    } else {
      setLoading(false)
    }
  }, [stateRound, location.search])

  useEffect(() => {
    if (!roundId) return
    setLoadingShots(true)
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Tenant-ID': tenantId,
    }
    const token = localStorage.getItem('accessToken')
    if (token) headers['Authorization'] = `Bearer ${token}`

    axios.get(
      withApiBaseUrl(`/api/v1/lottery/tenant/${tenantId}/yeekee/rounds/${roundId}/shots`),
      { headers }
    ).then((res) => {
      if (res.data?.success && Array.isArray(res.data.shots)) {
        setShots(res.data.shots)
      }
    }).catch(() => {}).finally(() => setLoadingShots(false))
  }, [roundId])

  const roundNum = round?.roundNumber?.split('-')[2] || '-'
  const maskMember = (raw: string): string => {
    const s = String(raw).trim()
    if (!s || s.length < 4) return s
    if (/^\d+$/.test(s)) {
      if (s.length >= 10) return s.slice(0, 3) + 'xxxxxx' + s.slice(-1)
      return s.slice(0, 2) + 'xxxxx' + s.slice(-1)
    }
    if (s.length <= 6) return s.slice(0, 1) + 'xxx' + s.slice(-1)
    return s.slice(0, 2) + 'xxxxx' + s.slice(-3)
  }

  // คล้ายบอท: บอทแสดงแบบ kaxxxxxng1, user แสดง username ก่อน (แบบ mask) ไม่มีค่อยใช้ mobile
  const botDisplayNameFallback = (id: string): string => {
    const letters = 'abcdefghijklmnopqrstuvwxyz'
    let n = 0
    for (let i = 0; i < (id || '').length; i++) n = (n * 31 + (id.charCodeAt(i) || 0)) >>> 0
    const r = () => { n = (n * 1103515245 + 12345) >>> 0; return n }
    const p = () => letters[r() % 26]
    return p() + p() + 'xxxxx' + p() + p() + String(r() % 10)
  }

  const getMemberName = (shot: YeekeeShotRow, index: number): string => {
    const isBot = String(shot.shooterType) === 'bot'
    if (isBot) {
      return shot.username && shot.username.includes('xxxxx')
        ? shot.username
        : botDisplayNameFallback(shot._id != null ? String(shot._id) : String(index))
    }
    // User: แสดง username ก่อน (mask) ไม่มีค่อยใช้ mobile ให้คล้ายรูปแบบบอท
    if (shot.username) return maskMember(shot.username)
    if (shot.displayPhoneNumber) return maskMember(shot.displayPhoneNumber)
    return 'สมาชิก'
  }

  if (loading) {
    return (
      <div className="content pb-3">
        <div className="container">
          <div className="row px--1">
            <div className="col-12 text-center py-5">
              <i className="fas fa-spinner fa-spin fs-24 c-white"></i>
              <p className="c-white mt-2">กำลังโหลด...</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!round) {
    return (
      <div className="content pb-3">
        <div className="container">
          <div className="row px--1">
            <div className="col-12 px-1">
              <div className="w-100 p-2 bgc-4 wrap justify-content-between">
                <a href="#" className="txt-link-d" onClick={(e) => { e.preventDefault(); navigate('/yeekee') }}>
                  <i className="fas fa-chevron-left me-1"></i>ย้อนกลับ
                </a>
              </div>
            </div>
            <div className="col-12 text-center py-5">
              <p className="c-white">ไม่พบข้อมูลรอบ</p>
              <button type="button" className="btn bgg-1 c-white bdr-5 mt-2" onClick={() => navigate('/yeekee')}>
                กลับหน้าจับยี่กี
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const closeTimeStr = round?.betEndTime
    ? new Date(round.betEndTime).toLocaleString('th-TH', {
        timeZone: 'Asia/Bangkok',
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : ''

  return (
    <div className="content pb-3">
      <div className="container">
        <div className="row px--1">
          <div className="col-12 px-1">
            <div className="bdr-5 bx-sd mb-2">
              <div className="row mx--1">
                <div className="col-12 px-1">
                  <div className="w-100 p-2 bgc-4 wrap justify-content-between">
                    <a
                      href="#"
                      className="txt-link-d"
                      onClick={(e) => { e.preventDefault(); navigate('/yeekee') }}
                    >
                      <i className="fas fa-chevron-left me-1"></i>
                      หน้าแทงหวย
                    </a>
                    <a
                      href="#"
                      className="bdr-5 c-white bgg-4 txt-link px-2"
                      onClick={(e) => { e.preventDefault(); navigate('/yeekee-terms', { state: { round } }) }}
                    >
                      กติกา & วิธีเล่น
                    </a>
                  </div>
                </div>

                <div className="col-12 px-1">
                  <div className="w-100 p-2 bgc-7">
                    <div className="bdr-5 bgc-4 c-red px-2 py-1 wrap justify-content-between">
                      <div className="wrap">
                        <div className="icon-f">
                          <img src={getImageUrl('logo.png')} alt="" />
                        </div>
                        <p className="mx-2 fs-18 t-medium">หวยยี่กี VIP</p>
                      </div>
                      <div className="bgc-9 bdr-5 py-1 px-2 c-white wrap justify-content-center">
                        <small>{closeTimeStr || `รอบที่ ${roundNum}`}</small>
                      </div>
                    </div>

                    <div className="wrap-box">
                      <div className="row mx--1">
                        {/* โครงตาม terms.html / รูปที่ 2 - แสดงเสมอ */}
                        {/* <div className="col-12 px-1 mt-2">
                          <div className="bg-white bdr-5 bdc-1 p-1 text-center">
                            <div className="bgg-1 bdr-5 px-1 t-regular c-white">
                              <small>รางวันที่ 1</small>
                            </div>
                            <div className="px-1 t-medium">
                              <h4>{result?.finalNumber || result?.sumTotal || '-'}</h4>
                            </div>
                          </div>
                        </div> */}

                        {/* <div className="col-6 col-lg-4 px-1 mt-2">
                          <div className="bg-white bdr-5 bdc-1 p-1 text-center">
                            <div className="bgg-1 bdr-5 px-1 t-regular c-white">
                              <small>3ตัวบน</small>
                            </div>
                            <div className="px-1">
                              <h5>{result?.threeDigitTop || '-'}</h5>
                            </div>
                          </div>
                        </div>
                        <div className="col-6 col-lg-4 px-1 mt-2">
                          <div className="bg-white bdr-5 bdc-1 p-1 text-center">
                            <div className="bgg-1 bdr-5 px-1 t-regular c-white">
                              <small>2ตัวล่าง</small>
                            </div>
                            <div className="px-1">
                              <h5>{result?.twoDigitDown ?? result?.twoDigitTop ?? '-'}</h5>
                            </div>
                          </div>
                        </div>
                        <div className="col-12 col-lg-4 px-1 mt-2">
                          <div className="bg-white bdr-5 bdc-1 p-1 text-center">
                            <div className="bgg-1 bdr-5 px-1 t-regular c-white">
                              <small>3ตัวล่าง</small>
                            </div>
                            <div className="px-1">
                              <h5>{(result as YeekeeResultData)?.threeDigitBottom ?? result?.threeDigitTop ?? '-'}</h5>
                            </div>
                          </div>
                        </div> */}

                        <div className="col-6 px-1 mt-2">
                          <div className="bg-white bdr-5 bdc-1 p-1 text-center">
                            <div className="bgg-1 bdr-5 px-1 t-regular c-white">
                              <small>3ตัวบน</small>
                            </div>
                            <div className="px-1">
                              <h5>{result?.threeDigitTop || '-'}</h5>
                            </div>
                          </div>
                        </div>
                        <div className="col-6 px-1 mt-2">
                          <div className="bg-white bdr-5 bdc-1 p-1 text-center">
                            <div className="bgg-1 bdr-5 px-1 t-regular c-white">
                              <small>2ตัวล่าง</small>
                            </div>
                            <div className="px-1">
                              <h5>{result?.twoDigitDown ?? result?.twoDigitTop ?? '-'}</h5>
                            </div>
                          </div>
                        </div>

                        <div className="col-12 px-1 mt-2">
                          <div className="wrap justify-content-center bgc-4 bdr-5 c-white p-2 bx-sd">
                            <div className="w-100">
                              <h5 className="text-center t-regular c-dark bdc-1 px-3 bdr-10">
                                ผลรวมเลขและสมาชิกที่ร่วมออกรางวัล
                              </h5>
                              <h1 className="c-red text-center t-medium my-2">
                                {result?.sumTotal != null ? String(result.sumTotal) : '-'}
                              </h1>
                              {!result && (
                                <p className="text-center c-white small mb-2">
                                  รอผลการออกรางวัล (บอท yeekee-result-calculator จะคำนวณเมื่อครบ 25 ชุด)
                                </p>
                              )}
                            </div>
                            {loadingShots ? (
                              <p className="text-center c-white small mb-0">
                                <i className="fas fa-spinner fa-spin me-1"></i>กำลังโหลดรายการยิงเลข...
                              </p>
                            ) : (
                              <div className="table-responsive w-100 fs-14">
                                <table className="table table-striped mb-0 table-bordered text-center">
                                  <thead className="table-dark">
                                    <tr>
                                      <th>ลำดับ</th>
                                      <th>เลข</th>
                                      <th>สมาชิก</th>
                                      <th>เวลา</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {shots.length > 0 ? (
                                      shots.map((shot, index) => {
                                        const isPos5 = index === 4   // ยิงอันดับที่ 5 โบนัส 200
                                        const isPos16 = index === 15 // ยิงอันดับที่ 16 โบนัส 400 + ใช้ในสูตรคำนวณ
                                        const timeStr = shot.shotAt
                                          ? new Date(shot.shotAt).toLocaleTimeString('th-TH', {
                                              hour: '2-digit',
                                              minute: '2-digit',
                                              second: '2-digit',
                                            })
                                          : '-'
                                        return (
                                          <tr key={shot.number + '-' + index}>
                                            <td>
                                              {isPos5 ? (
                                                <div className="bgg-4 bdr-5 c-white d-inline px-2">
                                                  {index + 1}
                                                </div>
                                              ) : isPos16 ? (
                                                <div className="bgg-1 bdr-5 c-white d-inline px-2">
                                                  {index + 1}
                                                </div>
                                              ) : (
                                                index + 1
                                              )}
                                            </td>
                                            <td>{shot.number}</td>
                                            <td>{getMemberName(shot, index)}</td>
                                            <td>{timeStr}</td>
                                          </tr>
                                        )
                                      })
                                    ) : (
                                      <tr>
                                        <td colSpan={4} className="text-center py-3 c-dark">
                                          ยังไม่มีรายการยิงเลข
                                        </td>
                                      </tr>
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* ยิงลำดับที่ 5 (โบนัส 200), 16 (โบนัส 400 + ใช้ในสูตรคำนวณ) */}
                        <div className="col-6 col-md-6 px-1 mt-2">
                          <div className="bg-white bdr-5 bdc-1 p-1 text-center">
                            <div className="bgg-4 bdr-5 px-1 t-regular c-white">
                              <small>ยิงลำดับที่ 5 (โบนัส 200)</small>
                            </div>
                            <div className="px-1">
                              <h5>
                                {shots[4]
                                  ? `${getMemberName(shots[4], 4)}`
                                  : '-'}
                              </h5>
                            </div>
                          </div>
                        </div>
                        <div className="col-6 col-md-6 px-1 mt-2">
                          <div className="bg-white bdr-5 bdc-1 p-1 text-center">
                            <div className="bgg-1 bdr-5 px-1 t-regular c-white">
                              <small>ยิงลำดับที่ 16 (โบนัส 400)</small>
                            </div>
                            <div className="px-1">
                              <h5>
                                {shots[15]
                                  ? `${getMemberName(shots[15], 15)}`
                                  : '-'}
                              </h5>
                            </div>
                          </div>
                        </div>

                        {/* แสดงกติกาการออกผล (ผลคำนวณโดยบอท yeekee-result-calculator เท่านั้น) */}
                        {/* <div className="col-12 px-1 mt-3">
                          <div className="bg-white bdr-5 bdc-1 p-3">
                            <h6 className="t-medium c-dark border-bottom pb-2 mb-2">
                              วิธีการดูผลรางวัล &apos;หวยยี่กี VIP&apos;
                            </h6>
                            <p className="small mb-1">
                              ผลรางวัลจากการส่งตัวเลข 5 หลัก จำนวน {result?.totalShots ?? '-'} ชุด
                            </p>
                            <p className="small mb-1">
                              [ผลรวม] - [ชุดเลขลำดับที่ 16] = ผลลัพธ์รางวัล
                            </p>
                            <p className="small mb-1 text-muted">
                              ตัวอย่าง: 123456789 - 54321 = 123402468 → สามตัวบน (468) สองตัวล่าง (02)
                            </p>
                            {result && (
                              <p className="small mb-0 c-dark">
                                ผลรอบนี้: <strong>{result.sumTotal}</strong> - <strong>{(result as YeekeeResultData).position16Number ?? '-'}</strong> = <strong>{result.finalNumber}</strong> → สามตัวบน ({result.threeDigitTop}) สองตัวล่าง ({result.twoDigitDown ?? result.twoDigitTop ?? '-'})
                              </p>
                            )}
                          </div>
                        </div> */}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default YeekeeResultPage
